/**
 * DOCUMENT-UPLOAD-MODAL
 * Upload an existing prevention document
 */
import LaunchButton from "@/components/LaunchButton"
import Button from "@/components/button"
import Modal from "@/components/modal"
import TextInput from "@/components/text-input"
import { useAppDispatch, useAppSelector } from "@/core/root-reducer"
import documentStore from "../../model/document.store"
import { createEmptyDocument, Document } from "../../model/document.types"
import UploadButtonWidget from "@/widgets/upload-button.widget"
import { upload } from "@/redux/_upload.actions"
import fileToImage from "@/utils/file-to-image"
import env from "@/env"
import { toast } from "react-toastify"
import { useState } from "react"
import LoadingModal from "@/modals/loading.modal"
import { formatDate } from "@/utils/format-date.utils"
import { IconDefinition } from "@fortawesome/fontawesome-svg-core"
import { faChevronLeft, faFileLines, faListCheck } from "@fortawesome/free-solid-svg-icons"
import { v4 as uuid } from "uuid"
import { getDownloadFilename } from "../../utils/document-file.utils"
import { STATUS_SAVING } from "@/redux/_status.types"

interface Props {
  onClose: () => void
  document?: Document
  onDelete?: () => void
}

const DOCUMENT_TYPES: { id: Document["type"]; name: string; icon: IconDefinition }[] = [
  { id: "duerp", name: "Document unique", icon: faFileLines },
  { id: "papripact", name: "PAPRIPACT", icon: faListCheck }
]

function getFileExtension(file: File) {
  const extension = file.name.split(".").pop()
  return extension ? extension.toLowerCase() : file.type.split("/").pop() || "pdf"
}

export default function DocumentUploadModal(props: Props) {
  const dispatch = useAppDispatch()
  const status = useAppSelector(documentStore.selectors.status)
  const _session = useAppSelector((state) => state._session)
  const establishment = useAppSelector((state) => state.establishment.active)
  const isEditing = Boolean(props.document)
  const [document, setDocument] = useState<Document>(() =>
    props.document ?? createEmptyDocument({
      type: "duerp",
      EstablishmentId: _session.establishmentId ?? establishment.id,
      content: {}
    })
  )
  const [isSaving, setIsSaving] = useState(false)
  const [file, setFile] = useState<File | null>(null)
  const [step, setStep] = useState<"CHOICE" | "UPLOAD">(isEditing ? "UPLOAD" : "CHOICE")
  const canSave = !isSaving && Number.isFinite(new Date(document.date).getTime()) && (isEditing || Boolean(file))

  function edit(key: keyof Document, value: any) {
    setDocument((current) => ({
      ...current,
      [key]: value
    }))
  }

  async function save() {
    if (!canSave) return

    setIsSaving(true)
    try {
      let url = document.url
      if (file) {
        const fileContent = await fileToImage(file)
        const extension = getFileExtension(file)
        //Un nouveau chemin évite d'écraser le fichier actuel avant la sauvegarde et de servir une copie en cache.
        const fileId = isEditing ? uuid() : document.id
        const response: any = await dispatch(upload(fileId, fileContent, "Documents", file.type, extension))
        if (response?.error) throw response.error
        url = `${env.REACT_APP_URL_SPACE}/Documents/${fileId}.${extension}`
      }

      const savedDocument = { ...document, url }
      if (isEditing) {
        dispatch(documentStore.actions.setActive(savedDocument))
        await dispatch(documentStore.thunks.updateEntity()).unwrap()
      } else {
        await dispatch(documentStore.thunks.createEntity(savedDocument)).unwrap()
      }
      props.onClose()
    } catch {
      toast("Le document n'a pas pu être enregistré. Vous pouvez réessayer.", { type: "error" })
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Modal
      title={
        isEditing
          ? document.type === "duerp" ? "Modifier le document unique" : "Modifier le PAPRIPACT"
          : step === "CHOICE"
          ? "Uploader un document"
          : document.type === "duerp"
            ? "Uploader un document unique"
            : "Uploader un PAPRIPACT"
      }
      isCloseButtonVisible
      onClose={() => { if (!isSaving) props.onClose() }}
      onDelete={isEditing ? props.onDelete : undefined}
      onNext={step === "UPLOAD" ? save : undefined}
      onNextCta={isEditing ? "Enregistrer" : "Ajouter"}
      nextDisabled={!canSave}
      footerStart={
        step === "UPLOAD" && !isEditing && (
          <Button icon={faChevronLeft} onClick={() => setStep("CHOICE")}>
            Retour
          </Button>
        )
      }
      status={isSaving ? STATUS_SAVING : status}>
      {isSaving && <LoadingModal />}

      {step === "CHOICE" ? (
        <div className="flex gap-[12px]">
          {DOCUMENT_TYPES.map((item) => (
            <LaunchButton
              key={item.id}
              title={item.name}
              icon={item.icon}
              onClick={() => {
                edit("type", item.id)
                setStep("UPLOAD")
              }}
            />
          ))}
        </div>
      ) : (
        <>
          <TextInput
            type="date"
            isFullWidth
            title="Date du document"
            value={formatDate(document.date, false, true)}
            onChange={(e) => edit("date", new Date(e.value))}
          />

          {(file || document.url) && (
            <p className="grey-t mt-[20px] text-sm">
              {file ? `Fichier sélectionné : ${file.name}` : `Fichier actuel : ${getDownloadFilename(document)}`}
            </p>
          )}
          <div className="-m-[3px] mt-20">
            <UploadButtonWidget
              acceptedFiles=".pdf,.doc,.docx,.xls,.xlsx"
              onUpload={(files) => { if (files[0]) setFile(files[0]) }}
            />
          </div>
        </>
      )}
    </Modal>
  )
}
